import { AdminAbility } from '@repo/sdk/backoffice-abilities'
import {
	type BackofficeAdmin,
	type BackofficeAdminStatus,
	type BackofficeAuthExportAdminsData,
	type BackofficeAuthListAdminsData,
	backofficeAuthExportAdmins
} from '@repo/sdk/gen'
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/avatar'
import { Badge, type BadgeVariant } from '@repo/ui/badge'
import { DataTableColumnSettings } from '@repo/ui/components/data-table/column-settings'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListLayout } from '@repo/ui/components/list-layout'
import { formatLocaleDate } from '@repo/ui/dates'
import { formatError, getInitials } from '@repo/ui/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { type TKey, useTranslation } from '@/lib/i18n'
import { pageTitle } from '@/lib/page-title'
import { requireAdminPermission } from '@/modules/permissions/lib/require-admin-permission'
import { TableExportMenu } from '@/routes/(private)/-components/table-export-menu'
import { downloadExportResult } from '@/routes/(private)/-lib/export-download'
import { AdminActionsDropdown } from '@/routes/(private)/admins/-components/admin-actions-dropdown'
import { InviteAdminDialog } from '@/routes/(private)/admins/-components/invite-admin-dialog'
import { adminsListQueryOptions } from '@/routes/(private)/admins/-query/query-options'
import { usePageSize } from '@/stores/use-page-size'

type SortBody = NonNullable<BackofficeAuthListAdminsData['body']['sort']>
type SortBy = NonNullable<SortBody['by']>
type SortDirection = NonNullable<SortBody['direction']>

const SORT_BY_VALUES: readonly SortBy[] = ['createdAt', 'name'] as const
const SORT_DIRECTION_VALUES: readonly SortDirection[] = ['asc', 'desc'] as const
const EMPTY_ADMINS: BackofficeAdmin[] = []

const STATUS_LABEL_KEYS = {
	pending: 'admin-admins:status.pending',
	active: 'admin-admins:status.active',
	disabled: 'admin-admins:status.disabled'
} as const satisfies Record<BackofficeAdminStatus, TKey>

const STATUS_TONE = {
	pending: 'warning',
	active: 'success',
	disabled: 'destructive'
} as const satisfies Record<BackofficeAdminStatus, BadgeVariant>

const searchSchema = z.object({
	search: z.string().trim().default(''),
	by: z.enum(SORT_BY_VALUES).optional(),
	direction: z.enum(SORT_DIRECTION_VALUES).optional(),
	page: z.number().int().min(1).default(1)
})

export const Route = createFileRoute('/(private)/admins/')({
	beforeLoad: requireAdminPermission([AdminAbility.AdminsManage]),
	head: ({ match }) => ({
		meta: [
			{
				title: pageTitle(
					match.context.appLanguage,
					'admin-admins:page_title'
				)
			}
		]
	}),
	component: AdminAdmins,
	staticData: {
		breadcrumb: 'admin-admins:page_title'
	},
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	}
})

function AdminAdmins() {
	const { t } = useTranslation()
	const navigate = Route.useNavigate()
	const params = Route.useSearch()
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)

	const columns: DataTableColumn<BackofficeAdmin, SortBy>[] = [
		{
			id: 'name',
			header: t('common:name'),
			pinned: 'left',
			sortKey: 'name',
			cell: (dataItem) => (
				<div className='flex items-center gap-2'>
					<Avatar size='sm'>
						{dataItem.image?.url ? (
							<AvatarImage
								alt={dataItem.name ?? undefined}
								src={dataItem.image.url}
							/>
						) : null}
						<AvatarFallback>
							{getInitials(dataItem.name ?? '')}
						</AvatarFallback>
					</Avatar>
					<span className='truncate'>{dataItem.name}</span>
				</div>
			),
			minWidth: 140,
			flex: 1
		},
		{
			id: 'email',
			header: t('common:email'),
			cell: (dataItem) => dataItem.email,
			minWidth: 180,
			flex: 2
		},
		{
			id: 'status',
			header: t('admin-admins:columns.status'),
			cell: (dataItem) => (
				<Badge variant={STATUS_TONE[dataItem.status]}>
					{t(STATUS_LABEL_KEYS[dataItem.status])}
				</Badge>
			),
			minWidth: 110,
			flex: 1
		},
		{
			id: 'createdAt',
			header: t('common:created_at'),
			sortKey: 'createdAt',
			cell: (dataItem) =>
				formatLocaleDate(dataItem.createdAt, 'P HH:mm:ss'),
			minWidth: 160,
			flex: 1
		},
		{
			id: 'actions',
			header: '',
			label: t('admin-admins:columns.actions'),
			pinned: 'right',
			hideable: false,
			manageable: false,
			pinnable: false,
			enableResizing: false,
			cell: (dataItem) => (
				<div className='flex w-full justify-end'>
					<AdminActionsDropdown admin={dataItem} />
				</div>
			),
			width: 64,
			minWidth: 64,
			maxWidth: 64
		}
	]

	const columnsState = useDataTableColumnState(columns)

	const filters = {
		search: params.search || undefined,
		// Facility managers are managed from their residence's page.
		role: 'general_admin' as const
	}
	const sort =
		params.by === undefined
			? undefined
			: { by: params.by, direction: params.direction ?? 'asc' }

	const query = useQuery(
		adminsListQueryOptions({
			page: params.page,
			limit: pageSize,
			filters,
			sort
		})
	)

	const exportAdmins = useMutation({
		mutationFn: async (body: BackofficeAuthExportAdminsData['body']) =>
			downloadExportResult(
				await backofficeAuthExportAdmins({ body }),
				`backoffice-admins.${body.format}`
			),
		onSuccess: (result) => {
			if (result === 'downloaded') {
				toast.success(t('admin-exports:dialog.downloaded'))
				return
			}

			toast.success(t('admin-exports:dialog.started'))
		},
		onError: (error) => toast.error(formatError(error))
	})

	const handleExport = (
		format: BackofficeAuthExportAdminsData['body']['format']
	) => {
		exportAdmins.mutate({
			format,
			mode: 'auto',
			filters,
			sort
		})
	}

	return (
		<ListLayout
			controlsSlot={
				<>
					<ListInputSearch
						placeholder={t('admin-admins:search_placeholder')}
						resetKeys={['page']}
					/>
					<DataTableColumnSettings
						columns={columns}
						columnsState={columnsState}
					/>
				</>
			}
			headEndSlot={
				<div className='ml-auto flex items-center gap-2'>
					<TableExportMenu
						onSelectFormat={handleExport}
						pending={exportAdmins.isPending}
					/>
					<InviteAdminDialog />
				</div>
			}
			listSlot={
				<DataTable
					columns={columns}
					columnsState={columnsState}
					data={query.data?.data ?? EMPTY_ADMINS}
					pagination={{
						pageIndex: params.page - 1,
						pageCount: query.data?.meta.totalPages ?? 1,
						onPageChange: (next) =>
							navigate({
								search: (prev) => ({ ...prev, page: next + 1 }),
								replace: true
							}),
						pageSize,
						onPageSizeChange: (next) => {
							setPageSize(next)
							navigate({
								search: (prev) => ({ ...prev, page: 1 }),
								replace: true
							})
						}
					}}
					sort={{
						state: { by: params.by, direction: params.direction },
						onSortChange: (next) =>
							navigate({
								search: (prev) => ({
									...prev,
									by: next.by,
									direction: next.direction,
									page: 1
								}),
								replace: true
							})
					}}
					state={{
						loading: query.isPending || query.isPlaceholderData,
						error: query.isError
					}}
				/>
			}
			title={t('admin-admins:page_title')}
		/>
	)
}
